import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, ShieldCheck, ShieldAlert, Clock, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import httpClient from '@/api/httpClient';

export type CustomDomainStatus = 'pending_dns' | 'issuing' | 'active' | 'error';

export interface CustomDomainInfo {
    id: string;
    custom_domain?: string | null;
    custom_domain_status?: CustomDomainStatus | null;
    custom_domain_status_message?: string | null;
    custom_domain_checked_at?: string | null;
    custom_domain_hostnames?: string[];
    /** All "required" records must exist; at least one of "any_of" (trader./trade. are aliases). */
    custom_domain_hostname_rules?: { required: string[]; any_of: string[] };
    custom_domain_cname_target?: string | null;
    custom_domain_tls_managed?: boolean;
}

const STATUS_META: Record<
    CustomDomainStatus,
    {
        label: string;
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
        Icon: typeof Globe;
    }
> = {
    pending_dns: { label: 'Waiting for DNS', variant: 'outline', Icon: Clock },
    issuing: { label: 'Issuing certificate', variant: 'secondary', Icon: RefreshCw },
    active: { label: 'HTTPS active', variant: 'default', Icon: ShieldCheck },
    error: { label: 'Error', variant: 'destructive', Icon: ShieldAlert },
};

export function CustomDomainStatusBadge({ status }: { status?: CustomDomainStatus | null }) {
    if (!status || !STATUS_META[status]) {
        return null;
    }
    const { label, variant, Icon } = STATUS_META[status];
    return (
        <Badge variant={variant} className="gap-1 whitespace-nowrap">
            <Icon className="h-3 w-3" />
            {label}
        </Badge>
    );
}

interface CustomDomainTlsPanelProps {
    tenant: CustomDomainInfo;
    onUpdated: (tenant: Partial<CustomDomainInfo>) => void;
}

/**
 * Shows the TLS state of a tenant's custom domain, the CNAME records the tenant
 * must create, and a button to re-run the sync on demand.
 */
export function CustomDomainTlsPanel({ tenant, onUpdated }: CustomDomainTlsPanelProps) {
    const [isSyncing, setIsSyncing] = useState(false);

    if (!tenant.custom_domain) {
        return null;
    }

    const hostnames = tenant.custom_domain_hostnames ?? [];
    const rules = tenant.custom_domain_hostname_rules;
    const requirementOf = (host: string): string => {
        if (!rules) return '';
        if (rules.required.includes(host)) return 'required';
        if (rules.any_of.includes(host))
            return rules.any_of.length > 1 ? 'one of these' : 'required';
        return '';
    };
    const target = tenant.custom_domain_cname_target;

    const copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied');
        } catch {
            toast.error('Could not copy to clipboard');
        }
    };

    const syncNow = async () => {
        setIsSyncing(true);
        try {
            const response = await httpClient.post(
                `/tenant-manage/tenants/${tenant.id}/sync-domain`
            );
            const data = response.data;
            if (data.success) {
                onUpdated(data.data);
                toast.success('Custom domain re-checked');
            } else {
                throw new Error(data.message || 'Sync failed');
            }
        } catch (error: any) {
            console.error('Failed to sync custom domain:', error);
            toast.error(error?.response?.data?.message || 'Failed to re-check custom domain');
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="mt-2 rounded-md border p-3 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span className="font-medium">TLS certificate</span>
                    {tenant.custom_domain_tls_managed === false ? (
                        <Badge variant="outline">Not managed on this deployment</Badge>
                    ) : (
                        <CustomDomainStatusBadge status={tenant.custom_domain_status} />
                    )}
                </div>
                {tenant.custom_domain_tls_managed !== false && (
                    <Button size="sm" variant="outline" onClick={syncNow} disabled={isSyncing}>
                        <RefreshCw className={`h-4 w-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                        {isSyncing ? 'Checking…' : 'Re-check'}
                    </Button>
                )}
            </div>

            {tenant.custom_domain_status_message && (
                <p className="text-gray-600">{tenant.custom_domain_status_message}</p>
            )}
            {tenant.custom_domain_checked_at && (
                <p className="text-xs text-gray-500">
                    Last checked: <DateTimeFormatter date={tenant.custom_domain_checked_at} />
                </p>
            )}

            {hostnames.length > 0 && (
                <div>
                    <p className="font-medium mb-1">DNS records the tenant must create</p>
                    <table className="w-full text-xs font-mono">
                        <tbody>
                            {hostnames.map(host => (
                                <tr key={host} className="border-t">
                                    <td className="py-1 pr-2">{host}</td>
                                    <td className="py-1 pr-2 text-gray-500">CNAME</td>
                                    <td className="py-1 pr-2 text-gray-500 font-sans">
                                        {requirementOf(host)}
                                    </td>
                                    <td className="py-1 pr-2">
                                        {target ?? (
                                            <span className="text-gray-500">
                                                (cluster hostname)
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1 text-right">
                                        {target && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 px-2"
                                                onClick={() => copy(`${host} CNAME ${target}`)}
                                                aria-label={`Copy DNS record for ${host}`}
                                            >
                                                <Copy className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="mt-1 text-xs text-gray-500">
                        {rules && rules.any_of.length > 1
                            ? `All "required" records and at least one of the "one of these" records must resolve. `
                            : 'All records must resolve. '}
                        The certificate is issued automatically and only covers the records that
                        exist; adding a missing optional record later extends it. This can take a
                        few minutes after the DNS change.
                    </p>
                </div>
            )}
        </div>
    );
}
